import { loadConnectAndInitialize, StripeConnectInstance } from "@stripe/connect-js";
import { ConnectComponentsProvider, ConnectDisputesList } from "@stripe/react-connect-js";
import { useMount, useUnmount } from "ahooks";
import React, { useRef, useState } from "react";
import { api } from "../../../api/api";
import { config } from "../../../config";
import { Loader } from "../../Loader";
import { getSeller } from "@/utils/getSeller";

interface StripeSessionResponse {
    client_secret?: string;
    clientSecret?: string;
}

export const DisputesList = () => {
    const [stripeConnectInstance, setStripeConnectInstance] = useState<StripeConnectInstance | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string>();
    const seller = getSeller();
    const isMountedRef = useRef(true);

    const publicKey = seller.defaultGateway?.publicKey ?? config.stripe.publicKey;

    const fetchStripeSession = async (): Promise<string> => {
        const response = await api.post<StripeSessionResponse>(`/sellers/${seller.id}/stripeAccount/sessions`, {
            components: { disputes_list: { enabled: true } },
        });

        // Handle different possible key names for client_secret
        const clientSecret = response.data.client_secret ?? response.data.clientSecret;

        if (!clientSecret) {
            console.error("No client_secret in response:", response.data);
            throw new Error("Invalid session response - no client_secret");
        }

        return clientSecret;
    };

    useMount(() => {
        const initializeStripeConnect = async () => {
            if (!isMountedRef.current) return;

            setIsLoading(true);
            setError(undefined);

            if (!publicKey) {
                setError("Stripe is not configured for this account.");
                setIsLoading(false);
                return;
            }

            try {
                const instance = loadConnectAndInitialize({
                    publishableKey: publicKey,
                    fetchClientSecret: fetchStripeSession,
                    appearance: {
                        overlays: "dialog",
                        variables: {
                            // Base colors from Xola UI Kit
                            colorPrimary: "#1352C6", // blue.DEFAULT
                            colorBackground: "#ffffff", // white
                            colorText: "#222324", // black
                            colorDanger: "#FF5A5A", // red.DEFAULT
                            colorSecondaryText: "#505254", // gray.darker
                            colorBorder: "#E0E3E7", // gray.light
                            fontFamily: "Inter, system-ui, sans-serif",
                            fontSizeBase: "16px", // fontSize.base
                            borderRadius: "4px",
                            spacingUnit: "4px",
                        },
                    },
                });

                if (isMountedRef.current) {
                    setStripeConnectInstance(instance);
                }
            } catch (error_) {
                console.error("Failed to initialize Stripe Connect:", error_);
                if (isMountedRef.current) {
                    setError("Failed to initialize Stripe. Please try again.");
                }
            } finally {
                if (isMountedRef.current) {
                    setIsLoading(false);
                }
            }
        };

        initializeStripeConnect();
    });

    useUnmount(() => {
        isMountedRef.current = false;
    });

    if (isLoading) {
        return <Loader className="py-20" text="Loading disputes..." />;
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-20 text-center">
                <p className="text-danger">{error}</p>
            </div>
        );
    }

    if (!stripeConnectInstance) {
        return null;
    }

    return (
        <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
            <div className="disputes-list-container min-h-[400px] rounded-lg bg-white">
                <ConnectDisputesList />
            </div>
        </ConnectComponentsProvider>
    );
};
